import React from "react";
import { State } from "country-state-city";
import { truncateCommaSeparatedString } from "../../components/common/utlity/utility";
import numberToWord from "../../components/common/utlity/invoiceword";
import { AWS_URL } from "../../services/http.service";
import { getCurrencySymbol } from "../../components/common/utlity/options";

// ==================== UTILITY FUNCTIONS ====================
const formatCurrency = (val, currency = "INR") => {
  let num = 0;
  if (typeof val === "number") {
    num = val;
  } else if (typeof val === "string") {
    num = parseFloat(val);
    if (isNaN(num)) num = 0;
  }
  return `${getCurrencySymbol(currency)} ${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (str) => {
  if (!str) return "-";
  const date = new Date(str);
  if (isNaN(date)) return str;
  return date.toLocaleDateString();
};
// ==================== CALCULATE TAX FROM INCLUDING TAX ====================
const calculateTaxBreakdown = (
  finalAmount,
  cgst,
  sgst,
  igst,
  cess,
  intraState
) => {
  const toFloat = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  const cgstRate = toFloat(cgst);
  const sgstRate = toFloat(sgst);
  const igstRate = toFloat(igst);
  const cessRate = toFloat(cess);

  const totalTaxPercent = intraState
    ? igstRate + cessRate
    : cgstRate + sgstRate + cessRate;

  const finalCents = Math.round(finalAmount * 100);
  const divisor = 1 + totalTaxPercent / 100;

  let baseCents = Math.round(finalCents / divisor);

  let cgstCents = 0,
    sgstCents = 0,
    igstCents = 0,
    cessCents = 0;

  if (intraState) {
    igstCents = Math.round((baseCents * igstRate) / 100);
    cessCents = Math.round((baseCents * cessRate) / 100);
  } else {
    cgstCents = Math.round((baseCents * cgstRate) / 100);
    sgstCents = Math.round((baseCents * sgstRate) / 100);
    cessCents = Math.round((baseCents * cessRate) / 100);
  }

  const sumTax = cgstCents + sgstCents + igstCents + cessCents;
  const diff = finalCents - (baseCents + sumTax);
  baseCents += diff; // adjust rounding

  const toR = (c) => Math.round(c) / 100;

  return {
    baseAmount: toR(baseCents),
    cgstAmount: toR(cgstCents),
    sgstAmount: toR(sgstCents),
    igstAmount: toR(igstCents),
    cessAmount: toR(cessCents),
  };
};

// ==================== CALCULATE ITEM TOTALS ====================
const calculateItemTotals = (
  item = {},
  includingTax = false,
  isIntraState = false
) => {
  const toFloat = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  const qty = Math.max(0, toFloat(item.quantity));
  const salePrice = toFloat(item.salePrice);
  const finalPrice = toFloat(item.finalPrice);

  const discount = toFloat(item.discount || 0);
  const discountUnit = item.discountUnit || "%";

  const cgst = toFloat(item.cgst || 0);
  const sgst = toFloat(item.sgst_utgst || 0);
  const igst = toFloat(item.igst || 0);
  const cess = toFloat(item.cess || 0);

  const toCents = (v) => Math.round(v * 100);
  const toR = (c) => Math.round(c) / 100;

  let itemAmountCents = includingTax
    ? toCents(qty * finalPrice)
    : toCents(qty * salePrice);

  let discountCents = 0;
  if (
    discountUnit === "₹" ||
    discountUnit === "INR" ||
    discountUnit === "amount"
  ) {
    discountCents = toCents(discount);
  } else {
    discountCents = Math.round((itemAmountCents * discount) / 100);
  }
  if (discountCents > itemAmountCents) discountCents = itemAmountCents;

  const amountAfterDiscountCents = itemAmountCents - discountCents;

  const totalTaxPercent = isIntraState ? igst + cess : cgst + sgst + cess;

  let baseCents = 0,
    cgstC = 0,
    sgstC = 0,
    igstC = 0,
    cessC = 0,
    finalAmount = 0;

  if (includingTax) {
    const divisor = 1 + totalTaxPercent / 100;
    baseCents = Math.round(amountAfterDiscountCents / divisor);

    if (isIntraState) {
      igstC = Math.round((baseCents * igst) / 100);
      cessC = Math.round((baseCents * cess) / 100);
    } else {
      cgstC = Math.round((baseCents * cgst) / 100);
      sgstC = Math.round((baseCents * sgst) / 100);
      cessC = Math.round((baseCents * cess) / 100);
    }

    finalAmount = toR(amountAfterDiscountCents);
  } else {
    baseCents = amountAfterDiscountCents;

    if (isIntraState) {
      igstC = Math.round((baseCents * igst) / 100);
      cessC = Math.round((baseCents * cess) / 100);
    } else {
      cgstC = Math.round((baseCents * cgst) / 100);
      sgstC = Math.round((baseCents * sgst) / 100);
      cessC = Math.round((baseCents * cess) / 100);
    }

    finalAmount = toR(baseCents + cgstC + sgstC + igstC + cessC);
  }

  const totalTax = toR(cgstC + sgstC + igstC + cessC);

  return {
    itemAmount: toR(itemAmountCents),
    discountAmount: toR(discountCents),
    baseAmount: toR(baseCents),
    cgstAmount: toR(cgstC),
    sgstAmount: toR(sgstC),
    igstAmount: toR(igstC),
    cessAmount: toR(cessC),
    totalTax,
    finalAmount,
    perUnitBase: qty ? toR(baseCents / qty) : 0,
  };
};

// ==================== CALCULATE ADDITIONAL ADJUSTMENTS ====================
const calculateAdditionalAdjustments = (adjustments, baseAmount = 0) => {
  if (!Array.isArray(adjustments) || adjustments.length === 0) {
    return { totalAmount: 0, adjustments: [] };
  }

  let currentAmount = baseAmount;
  const calculatedAdjustments = [];

  adjustments.forEach((adj) => {
    const amount = parseFloat(adj.amount) || 0;
    let calculatedAmount = 0;

    if (adj.type === "%") {
      calculatedAmount = (currentAmount * amount) / 100;
    } else {
      calculatedAmount = amount;
    }

    calculatedAdjustments.push({ ...adj, calculatedAmount });

    // update current amount sequentially
    if (
      adj.type === "discount" ||
      (adj.label && adj.label.toLowerCase().includes("discount")) ||
      adj.amount < 0
    ) {
      currentAmount -= calculatedAmount;
    } else {
      currentAmount += calculatedAmount;
    }
  });

  const totalAmount = calculatedAdjustments.reduce((sum, adj) => {
    if (
      adj.type === "discount" ||
      (adj.label && adj.label.toLowerCase().includes("discount")) ||
      adj.amount < 0
    ) {
      return sum + -adj.calculatedAmount;
    } else {
      return sum + adj.calculatedAmount;
    }
  }, 0);

  return { totalAmount, adjustments: calculatedAdjustments };
};

// ==================== HEADER COMPONENT ====================
const InvoiceHeader = ({
  businessName,
  bussiness = {},
  phone,
  isNonGSTInvoice,
  includingTax,
  isReturnInvoice,
  isEstimate,
  isAdvancePayment,
}) => {
  return (
    <div className='bg-[#0B5844] text-white p-4 mb-6 rounded'>
      <div className='flex justify-between items-start'>
        <div className='flex items-start space-x-4'>
          <div className='w-20 h-20 bg-white rounded-full flex items-center justify-center p-1'>
            <img
              src={
                bussiness?.businesslogo
                  ? AWS_URL + bussiness.businesslogo
                  : "/default-logo.png"
              }
              alt='Business Logo'
              className='w-full h-full object-contain rounded-full'
              // crossOrigin='anonymous'
            />
          </div>
          <div>
            <h1 className='text-xl font-bold text-white'>
              {businessName || "Business Name"}
            </h1>
            <div className='text-sm text-white mt-1'>
              <p>{bussiness?.firstLine || "Business Address Line 1"}</p>
              <p>
                {bussiness?.city || "City"}, {bussiness?.pincode || "Pincode"}
              </p>
              {!isNonGSTInvoice && (
                <p>GSTIN : {bussiness?.gstNumber || "N/A"}</p>
              )}
              <p>Mobile : {phone || "N/A"}</p>
              <p>Email : {bussiness?.email || "N/A"}</p>
              {bussiness?.websitelink && (
                <p>Website : {bussiness.websitelink}</p>
              )}
            </div>
          </div>
        </div>

        <div className='text-right'>
          <h2 className='text-2xl font-bold text-white'>
            {isNonGSTInvoice ? "INVOICE" : "TAX INVOICE"}
          </h2>
          {includingTax && (
            <p className='text-white text-sm mt-1'>(Tax Included in Rate)</p>
          )}
          {isReturnInvoice && (
            <p className='text-white text-sm mt-1'>Return Invoice/Debit Note</p>
          )}
          {isEstimate && (
            <p className='text-white text-sm mt-1'>Estimate Invoice</p>
          )}
          {isAdvancePayment && (
            <p className='text-white text-sm mt-1'>Proforma Invoice</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== INVOICE DETAILS COMPONENT ====================
const InvoiceDetails = ({ item, paymentTerms }) => {
  const itemState = State.getStateByCodeAndCountry(item?.state, item?.country);

  return (
    <div className='mb-6 border-2 border-gray-400 rounded'>
      <div className='flex border-b-2 border-gray-400'>
        <div className='w-1/2 border-r-2 border-gray-400 p-3'>
          <div className='flex mb-2'>
            <span className='w-28 font-medium'>Invoice no</span>
            <span>: {item.invoiceId || "N/A"}</span>
          </div>
          <div className='flex mb-2'>
            <span className='w-28 font-medium'>Invoice Date</span>
            <span>: {formatDate(item.saleDate)}</span>
          </div>
          <div className='flex'>
            <span className='w-28 font-medium'>Terms</span>
            <span>: {paymentTerms}</span>
          </div>
        </div>

        <div className='w-1/2 p-3'>
          <div className='flex'>
            <span className='w-32 font-medium'>Place Of Supply</span>
            <span>
              : {itemState?.name || "N/A"} ({itemState?.isoCode || "N/A"})
            </span>
          </div>
          {item?.ewayBillNo && (
            <div className='flex mt-2'>
              <span className='w-32 font-medium'>E-Way Bill No</span>
              <span>: {item.ewayBillNo}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== BILLING & SHIPPING COMPONENT ====================
const BillingShipping = ({ item, isNonGSTInvoice }) => {
  const itemState = State.getStateByCodeAndCountry(item?.state, item?.country);

  return (
    <div className='mb-6 border-2 border-gray-400 rounded'>
      <div className='grid grid-cols-2'>
        <div className='border-r-2 border-gray-400'>
          <div className='p-3 border-b-2 border-gray-400'>
            <h3 className='font-bold text-gray-800'>Bill To</h3>
          </div>
          <div className='p-3'>
            <h4 className='font-bold text-gray-800 mb-1'>
              {item.customerName || "Customer Name"}
            </h4>
            <p className='text-sm'>{item.address || "Customer Address"}</p>
            <p className='text-sm'>
              {item.city || "City"}, {itemState?.name || "State"} -{" "}
              {item.pincode || "Pincode"}
            </p>
            {!isNonGSTInvoice && item.gstNumber && (
              <p className='text-sm'>GSTIN : {item.gstNumber}</p>
            )}
            {item.mobile && <p className='text-sm'>Mobile : {item.mobile}</p>}
          </div>
        </div>

        <div>
          <div className='p-3 border-b-2 border-gray-400'>
            <h3 className='font-bold text-gray-800'>Ship To</h3>
          </div>
          <div className='p-3'>
            <h4 className='font-bold text-gray-800 mb-1'>
              {item.deliveryCustomerName ||
                item.customerName ||
                "Customer Name"}
            </h4>
            <p className='text-sm'>
              {item.deliveryAddress || item.address || "Delivery Address"}
            </p>
            <p className='text-sm'>
              {item.deliveryCity || item.city || "City"},{" "}
              {item.deliveryState || itemState?.name || "State"} -{" "}
              {item.deliveryPincode || item.pincode || "Pincode"}
            </p>
            {!isNonGSTInvoice && item.deliveryGstNumber && (
              <p className='text-sm'>GSTIN : {item.deliveryGstNumber}</p>
            )}
            {item.deliveryMobile && (
              <p className='text-sm'>Mobile : {item.deliveryMobile}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== GST COLUMNS COMPONENT ====================
const GSTColumns = ({
  item = {},
  includingTax = false,
  isIntraState = false,
  bussiness = {},
}) => {
  const totals = calculateItemTotals(item, includingTax, isIntraState);

  const fmt = (value) =>
    formatCurrency(value, bussiness?.currency).replace(/^[^\d]+\s*/, "");

  if (isIntraState) {
    return (
      <>
        <td className='py-2 px-3 border-2 border-gray-400 text-center align-top'>{`${
          parseFloat(item.igst) || 0
        }%`}</td>
        <td className='py-2 px-3 border-2 border-gray-400 text-right align-top'>
          {fmt(totals.igstAmount)}
        </td>
        <td className='py-2 px-3 border-2 border-gray-400 text-center align-top'>{`${
          parseFloat(item.cess) || 0
        }%`}</td>
        <td className='py-2 px-3 border-2 border-gray-400 text-right align-top'>
          {fmt(totals.cessAmount)}
        </td>
      </>
    );
  } else {
    return (
      <>
        <td className='py-2 px-3 border-2 border-gray-400 text-center align-top'>{`${
          parseFloat(item.cgst) || 0
        }%`}</td>
        <td className='py-2 px-3 border-2 border-gray-400 text-right align-top'>
          {fmt(totals.cgstAmount)}
        </td>
        <td className='py-2 px-3 border-2 border-gray-400 text-center align-top'>{`${
          parseFloat(item.sgst_utgst) || 0
        }%`}</td>
        <td className='py-2 px-3 border-2 border-gray-400 text-right align-top'>
          {fmt(totals.sgstAmount)}
        </td>
        <td className='py-2 px-3 border-2 border-gray-400 text-center align-top'>{`${
          parseFloat(item.cess) || 0
        }%`}</td>
        <td className='py-2 px-3 border-2 border-gray-400 text-right align-top'>
          {fmt(totals.cessAmount)}
        </td>
      </>
    );
  }
};

// ==================== ITEM ROW COMPONENT ====================
const ItemRow = ({
  item,
  index,
  bussiness = {},
  includingTax = false,
  isEntireInvoiceNoGST = false,
  isIntraState = false,
}) => {
  const totals = calculateItemTotals(item, includingTax, isIntraState);
  console.log("Line no 446 item", item);
  const fmt = (value) =>
    formatCurrency(value, bussiness?.currency).replace(/^[^\d]+\s*/, "");

  return (
    <tr className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
      <td className='py-2 px-3 border-2 border-gray-400 text-center align-top'>
        {index + 1}
      </td>
      <td className='py-2 px-3 border-2 border-gray-400 align-top'>
        <div className='font-medium'>
          {item.productName || item.description || "Item"}
        </div>
        {item.description && item.description !== item.productName && (
          <div className='text-xs text-gray-600 mt-1'>{item.description}</div>
        )}
        <div className='text-xs text-gray-500 mt-1'>
          SAC:{" "}
          {truncateCommaSeparatedString(item.hsnNumber) || item.hsn || "N/A"}
        </div>
      </td>
      <td className='py-2 px-3 border-2 border-gray-400 text-center align-top'>
        <div>{item.quantity || 0}</div>
        <div className='text-xs text-gray-500'>{item.unitSize || "Nos"}</div>
      </td>
      <td className='py-2 px-3 border-2 border-gray-400 text-right align-top'>
        {fmt(item.salePrice)}
      </td>

      {!isEntireInvoiceNoGST && (
        <GSTColumns
          item={item}
          includingTax={includingTax}
          isIntraState={isIntraState}
          bussiness={bussiness}
        />
      )}

      <td className='py-2 px-3 border-2 border-gray-400 text-right align-top font-medium'>
        {totals.discountAmount > 0 && (
          <div className='text-xs text-red-600 mt-1'>
            -{fmt(totals.discountAmount)}
          </div>
        )}
      </td>
      <td className='py-2 px-3 border-2 border-gray-400 text-right align-top font-medium'>
        {fmt(item.salePrice)}
      </td>
    </tr>
  );
};

// ==================== ITEMS TABLE COMPONENT ====================
const ItemsTable = ({
  items = [],
  bussiness = {},
  includingTax = false,
  isEntireInvoiceNoGST = false,
  isIntraState = false,
}) => {
  return (
    <div className='mb-6 overflow-x-auto border-2 border-gray-400 rounded'>
      <table className='min-w-full border-collapse text-xs'>
        <thead>
          <tr className='bg-gray-100'>
            <th className='py-2 px-3 border-2 border-gray-400 text-left font-bold'>
              Sl. No
            </th>
            <th className='py-2 px-3 border-2 border-gray-400 text-left font-bold'>
              Item & Description
            </th>
            <th className='py-2 px-3 border-2 border-gray-400 text-center font-bold'>
              Qty
            </th>
            <th className='py-2 px-3 border-2 border-gray-400 text-center font-bold'>
              Rate
            </th>

            {!isEntireInvoiceNoGST && (
              <>
                {isIntraState ? (
                  <>
                    <th
                      className='py-2 px-3 border-2 border-gray-400 text-center font-bold'
                      colSpan='2'
                    >
                      IGST
                    </th>
                    <th
                      className='py-2 px-3 border-2 border-gray-400 text-center font-bold'
                      colSpan='2'
                    >
                      CESS
                    </th>
                  </>
                ) : (
                  <>
                    <th
                      className='py-2 px-3 border-2 border-gray-400 text-center font-bold'
                      colSpan='2'
                    >
                      CGST
                    </th>
                    <th
                      className='py-2 px-3 border-2 border-gray-400 text-center font-bold'
                      colSpan='2'
                    >
                      SGST
                    </th>
                    <th
                      className='py-2 px-3 border-2 border-gray-400 text-center font-bold'
                      colSpan='2'
                    >
                      CESS
                    </th>
                  </>
                )}
              </>
            )}

            <th className='py-2 px-3 border-2 border-gray-400 text-right font-bold'>
              Discount
            </th>
            <th className='py-2 px-3 border-2 border-gray-400 text-right font-bold'>
              Amount
            </th>
          </tr>

          {!isEntireInvoiceNoGST && (
            <tr className='bg-gray-50'>
              {isIntraState ? (
                <>
                  <th colSpan='4'></th>
                  <th className='py-1 px-2 border-2 border-gray-400 text-center font-bold'>
                    %
                  </th>
                  <th className='py-1 px-2 border-2 border-gray-400 text-center font-bold'>
                    Amt
                  </th>
                  <th className='py-1 px-2 border-2 border-gray-400 text-center font-bold'>
                    %
                  </th>
                  <th className='py-1 px-2 border-2 border-gray-400 text-center font-bold'>
                    Amt
                  </th>
                  <th colSpan='2'></th>
                </>
              ) : (
                <>
                  <th colSpan='4'></th>
                  <th className='py-1 px-2 border-2 border-gray-400 text-center font-bold'>
                    %
                  </th>
                  <th className='py-1 px-2 border-2 border-gray-400 text-center font-bold'>
                    Amt
                  </th>
                  <th className='py-1 px-2 border-2 border-gray-400 text-center font-bold'>
                    %
                  </th>
                  <th className='py-1 px-2 border-2 border-gray-400 text-center font-bold'>
                    Amt
                  </th>
                  <th className='py-1 px-2 border-2 border-gray-400 text-center font-bold'>
                    %
                  </th>
                  <th className='py-1 px-2 border-2 border-gray-400 text-center font-bold'>
                    Amt
                  </th>
                  <th colSpan='2'></th>
                </>
              )}
            </tr>
          )}
        </thead>

        <tbody>
          {items?.map((itm, idx) => (
            <ItemRow
              key={idx}
              item={itm}
              index={idx}
              bussiness={bussiness}
              includingTax={includingTax}
              isEntireInvoiceNoGST={isEntireInvoiceNoGST}
              isIntraState={isIntraState}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ==================== TOTALS ROW COMPONENT ====================
const TotalsRow = ({
  label,
  value,
  isBold = false,
  isRed = false,
  isGreen = false,
  isBlue = false,
  isOrange = false,
  isYellow = false,
}) => {
  const getTextColor = () => {
    if (isRed) return "text-red-600";
    if (isGreen) return "text-green-600";
    if (isBlue) return "text-blue-600";
    if (isOrange) return "text-orange-600";
    if (isYellow) return "text-yellow-600";
    return "text-gray-700";
  };

  const getBgColor = () => {
    if (isRed) return "bg-red-50";
    if (isGreen) return "bg-green-50";
    if (isBlue) return "bg-blue-50";
    if (isOrange) return "bg-orange-50";
    if (isYellow) return "bg-yellow-50";
    return "";
  };

  return (
    <tr className={getBgColor()}>
      <td
        className={`py-2 px-4 border-b border-gray-300 ${
          isBold ? "font-semibold" : "font-medium"
        } ${getTextColor()} text-xs`}
      >
        {label}
      </td>
      <td
        className={`py-2 px-4 border-b border-gray-300 text-right ${
          isBold ? "font-semibold" : "font-medium"
        } ${getTextColor()}`}
      >
        {value}
      </td>
    </tr>
  );
};

// ==================== ADDITIONAL ADJUSTMENTS COMPONENT ====================
const AdditionalAdjustments = ({ adjustments = [], type = "fee" }) => (
  <div className='text-xs text-gray-500 font-normal mt-1'>
    {adjustments?.map((adj, idx) => (
      <div
        key={idx}
        className={type === "discount" ? "text-red-600" : "text-green-600"}
      >
        • {adj.label}: {adj.amount}
        {adj.type === "%" ? "%" : ""}
        {adj.calculatedAmount !== undefined &&
          ` (${formatCurrency(adj.calculatedAmount, "INR")})`}
      </div>
    ))}
  </div>
);

// ==================== TOTALS TABLE COMPONENT ====================
const TotalsTable = ({
  invoiceData,
  item,
  bussiness,
  includingTax,
  isEntireInvoiceNoGST,
  isIntraState,
  isAdvancePayment,
  isReturnInvoice,
}) => {
  const {
    subtotal,
    totalItemDiscount,
    baseAmount,
    cgstTotal,
    sgstTotal,
    igstTotal,
    cessTotal,
    totalTaxAmount,
    additionalDiscounts,
    additionalFees,
    finalTotal,
    paidAmount,
    remainingBalance,
  } = invoiceData;
  const fmt = (value) => formatCurrency(value, bussiness?.currency);

  // EFFECTIVE TAX %
  const getRate = (taxAmount, base) =>
    base > 0 ? Math.round((taxAmount / base) * 10000) / 100 : 0;

  const cgstRate = getRate(cgstTotal, baseAmount);
  const sgstRate = getRate(sgstTotal, baseAmount);
  const igstRate = getRate(igstTotal, baseAmount);
  const cessRate = getRate(cessTotal, baseAmount);

  // ⭐ FIX: Final total for includedTax is ALWAYS subtotal (adjustments display-only)
  const grandTotal =
    subtotal + additionalFees?.totalAmount + additionalDiscounts?.totalAmount;

  return (
    <table className='w-full border-collapse border border-gray-300 rounded-lg text-sm'>
      <tbody>
        {/* TOTAL INCLUDING TAX */}
        <TotalsRow
          label={includingTax ? "Total Amount (Tax Included)" : "Sub Total"}
          value={fmt(subtotal)}
          isBold={true}
        />

        {/* ITEM DISCOUNT */}
        {totalItemDiscount > 0 && (
          <TotalsRow
            label='Item Discount'
            value={`-${fmt(totalItemDiscount)}`}
            isRed={true}
          />
        )}

        {/* BASE AMOUNT */}
        {includingTax && (
          <TotalsRow label='Base Amount (Before Tax)' value={fmt(baseAmount)} />
        )}

        {/* TAXES */}
        {!isEntireInvoiceNoGST && (
          <>
            {!isIntraState && cgstTotal > 0 && (
              <TotalsRow
                label={`CGST (${cgstRate}%) (Included in Rate)`}
                value={fmt(cgstTotal)}
              />
            )}

            {!isIntraState && sgstTotal > 0 && (
              <TotalsRow
                label={`SGST (${sgstRate}%) (Included in Rate)`}
                value={fmt(sgstTotal)}
              />
            )}

            {isIntraState && igstTotal > 0 && (
              <TotalsRow
                label={`IGST (${igstRate}%) (Included in Rate)`}
                value={fmt(igstTotal)}
              />
            )}

            {cessTotal > 0 && (
              <TotalsRow
                label={`CESS (${cessRate}%) (Included in Rate)`}
                value={fmt(cessTotal)}
              />
            )}

            {/* TOTAL TAX */}
            <TotalsRow
              label={`Total Tax (Included in Rate)`}
              value={fmt(totalTaxAmount)}
              isBold={true}
            />
          </>
        )}

        {/* ⭐ FIX: ADDITIONAL DISCOUNTS (DISPLAY ONLY) */}
        {additionalDiscounts?.adjustments?.length > 0 && (
          <tr>
            <td className='py-2 px-4 text-red-600'>
              <div className='text-xs'>Additional Discount </div>
              <AdditionalAdjustments
                adjustments={additionalDiscounts.adjustments}
                type='discount'
              />
            </td>
            <td className='py-2 px-4 text-right text-red-600'>
              -{fmt(additionalDiscounts.totalAmount)}
            </td>
          </tr>
        )}

        {/* ⭐ FIX: ADDITIONAL FEES (DISPLAY ONLY) */}
        {additionalFees?.adjustments?.length > 0 && (
          <tr>
            <td className='py-2 px-4 text-green-600'>
              <div className='text-xs'>Additional Fee </div>
              <AdditionalAdjustments
                adjustments={additionalFees.adjustments}
                type='fee'
              />
            </td>
            <td className='py-2 px-4 text-right text-green-600'>
              +{fmt(additionalFees.totalAmount)}
            </td>
          </tr>
        )}

        {/* GRAND TOTAL (FIXED) */}
        <tr className='bg-gray-50'>
          <td className='py-3 px-4 font-bold text-gray-900'>Grand Total</td>
          <td className='py-3 px-4 text-right font-bold text-gray-900'>
            {fmt(grandTotal)}
          </td>
        </tr>

        {/* PAYMENT */}
        {paidAmount > 0 && (
          <TotalsRow
            label='Payment Made'
            value={`(-) ${fmt(paidAmount)}`}
            isBlue={true}
          />
        )}

        {remainingBalance > 0 ? (
          <TotalsRow
            label='Balance Due'
            value={fmt(remainingBalance)}
            isOrange={true}
          />
        ) : (
          <TotalsRow label='Payment Status' value='Fully Paid' isGreen={true} />
        )}
      </tbody>
    </table>
  );
};

// ==================== AMOUNT IN WORDS COMPONENT ====================
const AmountInWords = ({ displayAmount = 0 }) => {
  const amount = Number(displayAmount) || 0;
  const rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);

  let words = numberToWord(rupees) + " Rupees";
  if (paise > 0) words += " and " + numberToWord(paise) + " Paise";
  words += " Only";

  return (
    <div className='border-2 border-gray-400 rounded p-3'>
      <h3 className='font-bold text-sm mb-1'>Total In Words</h3>
      <p className='text-sm italic'>{words}</p>
    </div>
  );
};

// ==================== NOTES COMPONENT ====================
const Notes = ({ notes = "" }) => (
  <div className='border-2 border-gray-400 rounded p-3'>
    <h3 className='font-bold text-sm mb-1'>Notes</h3>
    <p className='text-sm'>{notes}</p>
  </div>
);

// ==================== BANK DETAILS & QR & SIGNATURE COMPONENTS ====================
const BankDetails = ({ bussiness = {}, businessName = "" }) => {
  const safe = (v) => (v ? v : "N/A");

  return (
    <div className='border-2 border-gray-400 rounded p-3'>
      <h3 className='font-bold text-sm mb-2'>Bank Details</h3>
      <div className='text-sm space-y-1'>
        <p>
          <span className='font-medium'>Account Name:</span>{" "}
          {safe(businessName)}
        </p>
        {bussiness?.accountNumber && (
          <p>
            <span className='font-medium'>Account No:</span>{" "}
            {bussiness.accountNumber}
          </p>
        )}
        {bussiness?.ifscCode && (
          <p>
            <span className='font-medium'>IFSC Code:</span> {bussiness.ifscCode}
          </p>
        )}
        {bussiness?.bankName && (
          <p>
            <span className='font-medium'>Bank:</span> {bussiness.bankName}
          </p>
        )}
        {bussiness?.branchName && (
          <p>
            <span className='font-medium'>Branch:</span> {bussiness.branchName}
          </p>
        )}
      </div>
    </div>
  );
};

const QRCode = ({ bussiness = {}, businessName = "" }) => {
  const qrPath =
    bussiness?.aadharPhoto && AWS_URL
      ? `${AWS_URL}${bussiness.aadharPhoto}`
      : null;
  return (
    <div className='border-2 border-gray-400 rounded p-3'>
      <h3 className='font-semibold text-sm mb-2'>
        Payment QR Code For {businessName || "Business"}
      </h3>
      {qrPath ? (
        <img
          src={qrPath}
          alt='Payment QR Code'
          className='w-32 h-32 object-contain mx-auto'
          // crossOrigin='anonymous'
          onError={(e) => (e.target.style.display = "none")}
        />
      ) : (
        <p className='text-center text-xs text-gray-500'>No QR Available</p>
      )}
    </div>
  );
};

const BankDetailsAndQR = ({ bussiness = {}, businessName = "" }) => {
  const hasBank =
    bussiness?.accountNumber ||
    bussiness?.bankName ||
    bussiness?.ifscCode ||
    bussiness?.branchName;
  const hasQR = !!bussiness?.aadharPhoto;
  if (!hasBank && !hasQR) return null;
  return (
    <div className='grid grid-cols-2 gap-4'>
      {hasBank && (
        <BankDetails bussiness={bussiness} businessName={businessName} />
      )}
      {hasQR && <QRCode bussiness={bussiness} businessName={businessName} />}
    </div>
  );
};

const Signature = ({ bussiness = {}, businessName = "" }) => {
  const signaturePath =
    bussiness?.signature && AWS_URL ? `${AWS_URL}${bussiness.signature}` : null;
  return (
    <div className='text-center border border-gray-300 rounded-lg p-6 bg-white'>
      <div className='mb-3'>
        {signaturePath ? (
          <img
            src={signaturePath}
            alt='Signature'
            className='h-16 mx-auto object-contain'
            // crossOrigin='anonymous'
            onError={(e) => (e.target.style.display = "none")}
          />
        ) : (
          <div className='h-16 border-b-2 border-gray-400 mx-auto w-48'></div>
        )}
      </div>
      <p className='font-bold text-gray-900 mb-1'>
        For {businessName || "Business"}
      </p>
      <p className='text-sm text-gray-600'>Authorized Signature</p>
    </div>
  );
};

// ==================== AMOUNT SUMMARY COMPONENT ====================
const AmountSummary = ({
  invoiceData = {},
  item = {},
  bussiness = {},
  includingTax = false,
  isEntireInvoiceNoGST = false,
  isIntraState = false,
  isAdvancePayment = false,
  isReturnInvoice = false,
  businessName = "",
}) => {
  return (
    <div className='grid grid-cols-3 gap-6 mb-6'>
      <div className='col-span-2 space-y-4'>
        <AmountInWords displayAmount={invoiceData.displayAmount || 0} />
        {item.notes && <Notes notes={item.notes} />}
        <BankDetailsAndQR bussiness={bussiness} businessName={businessName} />
      </div>

      <div className='space-y-6'>
        <TotalsTable
          invoiceData={invoiceData}
          item={item}
          bussiness={bussiness}
          includingTax={includingTax}
          isEntireInvoiceNoGST={isEntireInvoiceNoGST}
          isIntraState={isIntraState}
          isAdvancePayment={isAdvancePayment}
          isReturnInvoice={isReturnInvoice}
        />
        <Signature bussiness={bussiness} businessName={businessName} />
      </div>
    </div>
  );
};

// ==================== HSN ROW, TOTAL & VERIFICATION ====================
const HSNRow = ({
  hsn = {},
  index = 0,
  bussiness = {},
  isIntraState = false,
}) => {
  const fmt = (value) =>
    formatCurrency(value || 0, bussiness?.currency).replace(/^[^\d]+\s*/, "");
  return (
    <tr className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
      <td className='py-2 px-3 border-2 border-gray-400'>{hsn.hsn || "N/A"}</td>
      <td className='py-2 px-3 border-2 border-gray-400 text-right'>
        {fmt(hsn.taxableValue)}
      </td>
      {isIntraState ? (
        <>
          <td className='py-2 px-3 border-2 border-gray-400 text-center'>{`${
            hsn.igstRate || 0
          }%`}</td>
          <td className='py-2 px-3 border-2 border-gray-400 text-right'>
            {fmt(hsn.igstAmount)}
          </td>
        </>
      ) : (
        <>
          <td className='py-2 px-3 border-2 border-gray-400 text-center'>{`${
            hsn.cgstRate || 0
          }%`}</td>
          <td className='py-2 px-3 border-2 border-gray-400 text-right'>
            {fmt(hsn.cgstAmount)}
          </td>
          <td className='py-2 px-3 border-2 border-gray-400 text-center'>{`${
            hsn.sgstRate || 0
          }%`}</td>
          <td className='py-2 px-3 border-2 border-gray-400 text-right'>
            {fmt(hsn.sgstAmount)}
          </td>
        </>
      )}
      <td className='py-2 px-3 border-2 border-gray-400 text-right'>
        {fmt(hsn.cessAmount)}
      </td>
      <td className='py-2 px-3 border-2 border-gray-400 text-center font-medium'>
        {fmt(hsn.totalTax)}
      </td>
    </tr>
  );
};

const HSNTotalRow = ({
  isIntraState = false,
  bussiness = {},
  totalTaxableValueHSN = 0,
  cgstTotal = 0,
  sgstTotal = 0,
  igstTotal = 0,
  cessTotal = 0,
  totalTaxAmountHSN = 0,
}) => {
  const fmt = (value) =>
    formatCurrency(value || 0, bussiness?.currency).replace(/^[^\d]+\s*/, "");
  return (
    <tr className='font-bold bg-gray-100'>
      <td className='py-2 px-3 border-2 border-gray-400'>Total</td>
      <td className='py-2 px-3 border-2 border-gray-400 text-right'>
        {fmt(totalTaxableValueHSN)}
      </td>
      {isIntraState ? (
        <>
          <td className='py-2 px-3 border-2 border-gray-400 text-center'></td>
          <td className='py-2 px-3 border-2 border-gray-400 text-right'>
            {fmt(igstTotal)}
          </td>
        </>
      ) : (
        <>
          <td className='py-2 px-3 border-2 border-gray-400 text-center'></td>
          <td className='py-2 px-3 border-2 border-gray-400 text-right'>
            {fmt(cgstTotal)}
          </td>
          <td className='py-2 px-3 border-2 border-gray-400 text-center'></td>
          <td className='py-2 px-3 border-2 border-gray-400 text-right'>
            {fmt(sgstTotal)}
          </td>
        </>
      )}
      <td className='py-2 px-3 border-2 border-gray-400 text-right'>
        {fmt(cessTotal)}
      </td>
      <td className='py-2 px-3 border-2 border-gray-400 text-center'>
        {fmt(totalTaxAmountHSN)}
      </td>
    </tr>
  );
};

const VerificationSection = ({
  totalTaxableValueHSN = 0,
  totalTaxAmountHSN = 0,
  bussiness = {},
}) => (
  <div className='mt-2 text-xs text-gray-600'>
    <p>
      <strong>Verification:</strong> Taxable Amount (
      {formatCurrency(totalTaxableValueHSN, bussiness?.currency)}) + Total Tax (
      {formatCurrency(totalTaxAmountHSN, bussiness?.currency)}) = Total Amount (
      {formatCurrency(
        totalTaxableValueHSN + totalTaxAmountHSN,
        bussiness?.currency
      )}
      )
    </p>
  </div>
);

// ==================== HSN SUMMARY ====================
const HSNSummary = ({
  hsnSummaryArray = [],
  bussiness = {},
  isIntraState = false,
  includingTax = false,
  totalTaxableValueHSN = 0,
  totalTaxAmountHSN = 0,
  cgstTotal = 0,
  sgstTotal = 0,
  igstTotal = 0,
  cessTotal = 0,
}) => {
  return (
    <div className='mt-8 border-2 border-gray-400 rounded p-4'>
      <h3 className='font-bold text-sm mb-2'>HSN/SAC Summary:</h3>
      <table className='min-w-full border-collapse border-2 border-gray-400 text-xs'>
        <thead>
          <tr className='bg-gray-100'>
            <th className='py-2 px-3 border-2 border-gray-400 text-left font-bold'>
              HSN/SAC
            </th>
            <th className='py-2 px-3 border-2 border-gray-400 text-right font-bold'>
              Taxable Amount
            </th>
            {isIntraState ? (
              <>
                <th className='py-2 px-3 border-2 border-gray-400 text-center font-bold'>
                  IGST Rate
                </th>
                <th className='py-2 px-3 border-2 border-gray-400 text-center font-bold'>
                  IGST Amount
                </th>
              </>
            ) : (
              <>
                <th className='py-2 px-3 border-2 border-gray-400 text-center font-bold'>
                  CGST Rate
                </th>
                <th className='py-2 px-3 border-2 border-gray-400 text-center font-bold'>
                  CGST Amount
                </th>
                <th className='py-2 px-3 border-2 border-gray-400 text-center font-bold'>
                  SGST Rate
                </th>
                <th className='py-2 px-3 border-2 border-gray-400 text-center font-bold'>
                  SGST Amount
                </th>
              </>
            )}
            <th className='py-2 px-3 border-2 border-gray-400 text-center font-bold'>
              Cess Amount
            </th>
            <th className='py-2 px-3 border-2 border-gray-400 text-center font-bold'>
              Total Tax Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {hsnSummaryArray.length > 0 ? (
            hsnSummaryArray.map((hsn, idx) => (
              <HSNRow
                key={idx}
                hsn={hsn}
                index={idx}
                bussiness={bussiness}
                isIntraState={isIntraState}
              />
            ))
          ) : (
            <tr>
              <td
                colSpan={isIntraState ? 5 : 7}
                className='text-center py-3 text-gray-500'
              >
                No HSN/SAC Data Available
              </td>
            </tr>
          )}
          <HSNTotalRow
            isIntraState={isIntraState}
            bussiness={bussiness}
            totalTaxableValueHSN={totalTaxableValueHSN}
            cgstTotal={cgstTotal}
            sgstTotal={sgstTotal}
            igstTotal={igstTotal}
            cessTotal={cessTotal}
            totalTaxAmountHSN={totalTaxAmountHSN}
          />
        </tbody>
      </table>
      {includingTax && (
        <VerificationSection
          totalTaxableValueHSN={totalTaxableValueHSN}
          totalTaxAmountHSN={totalTaxAmountHSN}
          bussiness={bussiness}
        />
      )}
    </div>
  );
};

// ==================== TERMS & DECLARATION COMPONENT ====================
const TermsDeclaration = ({ darkMode = false }) => {
  return (
    <div className='mt-4 pt-4 border-t border-gray-300'>
      <h3
        className={`text-sm font-semibold mb-2 ${
          darkMode ? "text-white" : "text-gray-700"
        }`}
      >
        Terms & Declaration
      </h3>
      <div
        className={`text-xs space-y-2 ${
          darkMode ? "text-gray-400" : "text-gray-600"
        }`}
      >
        <p>
          <strong>Declaration:</strong>
        </p>
        <ul className='list-disc list-inside space-y-1'>
          <li>
            We declare that this invoice shows the actual price of the goods
            described
          </li>
          <li>
            All particulars shown are true and correct to the best of our
            knowledge
          </li>
        </ul>
        <p>
          <strong>Terms:</strong>
        </p>
        <ul className='list-disc list-inside space-y-1'>
          <li>Payment due within 30 days from invoice date</li>
        </ul>
      </div>
    </div>
  );
};

// ==================== MAIN PDF INVOICE COMPONENT ====================
const PDFStyleInvoice = ({
  item,
  containerRef,
  businessName = "",
  phone = "",
  darkMode = false,
  bussiness = {},
}) => {
  console.log("Line 1330", item);
  if (!item) return <div>No invoice data available</div>;

  const state = State.getStateByCodeAndCountry(
    bussiness?.state,
    bussiness?.country
  );

  const isIntraState = !!item?.intraState;

  const includingTax =
    item?.includingTax || (item?.items && item?.items[0]?.taxIncluded) || false;

  const itemsToUse = Array.isArray(
    item?.invoiceId?.startsWith("RT") ? item?.returnitems : item?.items
  )
    ? item?.invoiceId?.startsWith("RT")
      ? item?.returnitems
      : item?.items
    : [];

  // ----------------------------------------------------
  // 1. ITEM LEVEL TOTALS
  // ----------------------------------------------------
  const itemTotals = (itemsToUse || []).map((it) =>
    calculateItemTotals(it, includingTax, isIntraState)
  );

  // Subtotal (usually item final amount incl. GST)
  const subtotal = itemTotals.reduce(
    (sum, total) => sum + (total.itemAmount || 0),
    0
  );

  const totalItemDiscount = itemTotals.reduce(
    (sum, total) => sum + (total.discountAmount || 0),
    0
  );

  // Base amount (before tax) – from itemTotals
  const baseAmount = itemTotals.reduce(
    (sum, total) => sum + (total.baseAmount || 0),
    0
  );

  // Taxable amount
  const taxableAmount = includingTax
    ? baseAmount
    : itemTotals.reduce((sum, total) => sum + (total.taxableValue || 0), 0);

  // Tax totals
  const cgstTotal = itemTotals.reduce(
    (sum, total) => sum + (total.cgstAmount || 0),
    0
  );
  const sgstTotal = itemTotals.reduce(
    (sum, total) => sum + (total.sgstAmount || 0),
    0
  );
  const igstTotal = itemTotals.reduce(
    (sum, total) => sum + (total.igstAmount || 0),
    0
  );
  const cessTotal = itemTotals.reduce(
    (sum, total) => sum + (total.cessAmount || 0),
    0
  );

  const totalTaxAmount = cgstTotal + sgstTotal + igstTotal + cessTotal;

  // ----------------------------------------------------
  // 2. ADDITIONAL DISCOUNTS / FEES & FINAL TOTAL
  // ----------------------------------------------------
  let finalTotal = 0;
  let additionalDiscounts = { totalAmount: 0, adjustments: [] };
  let additionalFees = { totalAmount: 0, adjustments: [] };

  const subscriptionDisc = parseFloat(item.subscriptionDisc) || 0;

  if (includingTax) {
    // Amount already includes GST
    const amountAfterItemDiscount = subtotal - totalItemDiscount;

    // Additional adjustments are applied on this amount
    additionalDiscounts = calculateAdditionalAdjustments(
      item?.additionalDiscount,
      amountAfterItemDiscount
    ) || { totalAmount: 0, adjustments: [] };

    additionalFees = calculateAdditionalAdjustments(
      item?.additionalFee,
      amountAfterItemDiscount - (additionalDiscounts?.totalAmount || 0)
    ) || { totalAmount: 0, adjustments: [] };

    const extraDiscount = additionalDiscounts?.totalAmount || 0;
    const extraFee = additionalFees?.totalAmount || 0;

    // 👉 FINAL TOTAL (tax already in subtotal)
    const expectedTotal =
      amountAfterItemDiscount - extraDiscount + extraFee - subscriptionDisc;

    finalTotal = Math.round(expectedTotal * 100) / 100;
  } else {
    // Tax is added on top of taxable value
    additionalDiscounts = calculateAdditionalAdjustments(
      item?.additionalDiscount,
      taxableAmount
    ) || { totalAmount: 0, adjustments: [] };

    additionalFees = calculateAdditionalAdjustments(
      item?.additionalFee,
      taxableAmount - (additionalDiscounts?.totalAmount || 0)
    ) || { totalAmount: 0, adjustments: [] };

    const extraDiscount = additionalDiscounts?.totalAmount || 0;
    const extraFee = additionalFees?.totalAmount || 0;

    const adjustedTaxable = taxableAmount - extraDiscount + extraFee;

    finalTotal = adjustedTaxable + totalTaxAmount - subscriptionDisc;

    finalTotal = Math.round(finalTotal * 100) / 100;
  }

  // ----------------------------------------------------
  // 3. PAYMENT & DISPLAY AMOUNTS
  // ----------------------------------------------------
  const paidAmount =
    (parseFloat(item.transactionAmount) || 0) -
    (parseFloat(item.remainingTransactionAmount) || 0);

  const remainingBalance = parseFloat(item.remainingTransactionAmount) || 0;

  const isReturnInvoice = item?.invoiceId?.startsWith("RT");
  const isEstimate = item?.saleType === "estimate";
  const isAdvancePayment = item?.saleType === "advancePayment";
  const isNonGSTInvoice = item?.showGST === false;

  const advancePayment = parseFloat(item.advancePayment || 0) || 0;

  const displayAmount = isReturnInvoice
    ? item.totalReturnAmount
    : isAdvancePayment || item?.saleType === "advanceSale"
    ? finalTotal - advancePayment
    : finalTotal;

  const paymentTerms = remainingBalance > 0 ? "Due On Receipt" : "Paid";

  // ----------------------------------------------------
  // 4. HSN SUMMARY
  // ----------------------------------------------------
  const hsnSummary = (itemsToUse || []).reduce((acc, itm, index) => {
    const hsn = itm.hsnNumber || itm.hsn || "N/A";
    const totals = itemTotals[index] || {
      baseAmount: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
      cessAmount: 0,
      totalTax: 0,
    };

    if (!acc[hsn]) {
      acc[hsn] = {
        hsn,
        taxableValue: 0,
        cgstRate: !isIntraState ? parseFloat(itm.cgst || 0) : 0,
        sgstRate: !isIntraState ? parseFloat(itm.sgst_utgst || 0) : 0,
        igstRate: isIntraState ? parseFloat(itm.igst || 0) : 0,
        cessRate: parseFloat(itm.cess || 0),
        cgstAmount: 0,
        sgstAmount: 0,
        igstAmount: 0,
        cessAmount: 0,
        totalTax: 0,
      };
    }

    acc[hsn].taxableValue += totals.baseAmount || 0;
    acc[hsn].cgstAmount += totals.cgstAmount || 0;
    acc[hsn].sgstAmount += totals.sgstAmount || 0;
    acc[hsn].igstAmount += totals.igstAmount || 0;
    acc[hsn].cessAmount += totals.cessAmount || 0;
    acc[hsn].totalTax += totals.totalTax || 0;

    return acc;
  }, {});

  const hsnSummaryArray = Object.values(hsnSummary || {});

  const totalTaxableValueHSN = hsnSummaryArray.reduce(
    (sum, hsn) => sum + (hsn.taxableValue || 0),
    0
  );

  const totalTaxAmountHSN = hsnSummaryArray.reduce(
    (sum, hsn) => sum + (hsn.totalTax || 0),
    0
  );

  // ----------------------------------------------------
  // 5. INVOICE DATA PASSED TO SUMMARY / TOTALS TABLE
  // ----------------------------------------------------
  const invoiceData = {
    subtotal,
    totalItemDiscount,
    taxableAmount: includingTax ? baseAmount : taxableAmount,
    baseAmount,
    cgstTotal,
    sgstTotal,
    igstTotal,
    cessTotal,
    totalTaxAmount,
    additionalDiscounts,
    additionalFees,
    finalTotal, // ✅ This is the full grand total (items + tax + extra fee/discount - subscription)
    displayAmount,
    paymentTerms,
    hsnSummaryArray,
    totalTaxableValueHSN,
    totalTaxAmountHSN,
    paidAmount,
    remainingBalance,
  };

  // ----------------------------------------------------
  // 6. RENDER
  // ----------------------------------------------------
  return (
    <div
      ref={containerRef}
      className='max-w-4xl mx-auto p-6 bg-white text-gray-800'
      style={{
        boxSizing: "border-box",
        width: "100%",
        fontFamily: "Arial, sans-serif",
        fontSize: "12px",
      }}
    >
      <InvoiceHeader
        businessName={businessName}
        bussiness={bussiness}
        phone={phone}
        isNonGSTInvoice={isNonGSTInvoice}
        includingTax={includingTax}
        isReturnInvoice={isReturnInvoice}
        isEstimate={isEstimate}
        isAdvancePayment={isAdvancePayment}
      />

      <InvoiceDetails item={item} paymentTerms={paymentTerms} />

      <BillingShipping item={item} isNonGSTInvoice={isNonGSTInvoice} />

      <ItemsTable
        items={itemsToUse}
        bussiness={bussiness}
        includingTax={includingTax}
        isEntireInvoiceNoGST={isNonGSTInvoice}
        isIntraState={isIntraState}
      />

      <AmountSummary
        invoiceData={invoiceData}
        item={item}
        bussiness={bussiness}
        includingTax={includingTax}
        isEntireInvoiceNoGST={isNonGSTInvoice}
        isIntraState={isIntraState}
        isAdvancePayment={isAdvancePayment}
        isReturnInvoice={isReturnInvoice}
        businessName={businessName}
      />

      {!isNonGSTInvoice && (
        <HSNSummary
          hsnSummaryArray={hsnSummaryArray}
          bussiness={bussiness}
          isIntraState={isIntraState}
          includingTax={includingTax}
          totalTaxableValueHSN={totalTaxableValueHSN}
          totalTaxAmountHSN={totalTaxAmountHSN}
          cgstTotal={cgstTotal}
          sgstTotal={sgstTotal}
          igstTotal={igstTotal}
          cessTotal={cessTotal}
        />
      )}

      <TermsDeclaration darkMode={darkMode} />
    </div>
  );
};

export default PDFStyleInvoice;
